import { useEffect, useRef } from 'react';
import { devWarning } from '@tanium/coreui-utils';

/**
 * Hook which returns a stable object based on the object provided on the first
 * render. Issues a dev-mode-only warning when any of the object's given
 * properties is not referentially stable between renders.
 *
 * Use this hook when you require that some values remain constant across all
 * renders, and want to alert developers if they violate this constraint.
 *
 * @param values An object containing values that should remain referentially
 * stable between renders. If a given property's value differs from that of a
 * previous render, a dev-only warning is issued.
 */
export const useInitialValues = <T extends object>(values: T) => {
  const storedValueRef = useRef(values);

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- the above condition is invariant
    useEffect(() => {
      Object.entries(values).forEach(([propName, value]) => {
        const storedValue = storedValueRef.current[propName as keyof T];
        if (value !== storedValue) {
          devWarning(
            false,
            `The ${propName} prop changed from ${storedValue} to ${value}. This is undefined behavior. The ${propName} prop must maintain a single, constant value across all renders.`,
          );
        }
      });
    }, [values]);
  }

  return storedValueRef.current;
};
